import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * A content section. By default it has NO visual chrome — content flows
 * directly on the page canvas with just the eyebrow / title / subtitle
 * structure for hierarchy. Tinted variants (blue / violet / amber /
 * emerald / rose / muted) wrap the section in a soft color block for
 * sections that genuinely need emphasis.
 *
 * Sits in a vertical stack inside <PageShell>.
 */
const VARIANTS = {
  default: "",
  muted: "rounded-2xl bg-stone-200 p-6",
  blue: "rounded-2xl bg-sky-100 p-6",
  violet: "rounded-2xl bg-violet-100 p-6",
  amber: "rounded-2xl bg-amber-100 p-6",
  emerald: "rounded-2xl bg-emerald-100 p-6",
  rose: "rounded-2xl bg-rose-100 p-6",
} as const;

export type SectionVariant = keyof typeof VARIANTS;

export type SectionCardProps = {
  /** Optional small uppercase label above the content. */
  eyebrow?: string;
  /** Optional title rendered larger, below the eyebrow. */
  title?: string;
  /** Optional muted one-line subtitle under the title. */
  subtitle?: string;
  /** Visual variant — use tinted backgrounds for emphasis. */
  variant?: SectionVariant;
  /** Content. */
  children: ReactNode;
  /** Extra classes merged into the card. */
  className?: string;
};

export function SectionCard({
  eyebrow,
  title,
  subtitle,
  variant = "default",
  children,
  className,
}: SectionCardProps) {
  const hasHeader = Boolean(eyebrow || title || subtitle);

  return (
    <section className={cn(VARIANTS[variant], className)}>
      {eyebrow ? (
        <p
          className={cn(
            "text-[10px] font-semibold uppercase tracking-[0.22em]",
            "text-stone-500",
            title || subtitle ? "mb-2" : "mb-0",
          )}
        >
          {eyebrow}
        </p>
      ) : null}

      {title ? (
        <h2
          className={cn(
            "text-lg font-semibold tracking-tight text-stone-900",
            "leading-[1.35]",
          )}
        >
          {title}
        </h2>
      ) : null}

      {subtitle ? (
        <p className="mt-1 text-sm text-stone-600 leading-[1.65]">
          {subtitle}
        </p>
      ) : null}

      {hasHeader ? <div className="h-4" /> : null}

      {children}
    </section>
  );
}
